import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { uniqBy } from 'lodash';
import { ISpecialist, SpecialistStore } from './types';
import { SPECIALISTS_COUNT_PER_PAGE } from './constants';
import { fetchSpecialistsBackupList, fetchSpecialistsFilters, fetchSpecialistsList } from './thunk';
import { addSpecialistToFavourite, removeSpecialistFromFavourite } from '../favouriteSpecialits/thunk';
import { updateArray } from '../../utils/updateArray';
import { fetchLastContract, fetchSpecialistByID } from '../loadedSpecialists/thunk';
import { FilterItem, FilterValue, Sort } from '@mindly/ui-components';
import { updateReview } from '../specialistReviews/thunk';
import { getSavedSort } from 'src/shared/utils/filters';

export const initialState: SpecialistStore = {
  listData: null,
  loading: 'idle',
  offset: 0,
  limit: SPECIALISTS_COUNT_PER_PAGE,
  isPaginationDisabled: false,
  filters: {
    values: {},
    loading: 'idle',
    items: [],
  },
  sort: getSavedSort() ?? {
    value: 'is_new',
    direction: 'desc',
  },
  backup: {
    listData: [],
    loading: 'idle',
  },
  goals: [],
};

interface UpdatePayload {
  field: keyof ISpecialist;
  value: any;
  data: Partial<ISpecialist>;
}

const specialistsReducer = createSlice({
  name: 'specialists',
  initialState,
  reducers: {
    resetSpecialist: () => initialState,
    resetOffset: (state) => {
      state.offset = 0;
    },
    resetPagination: (state) => {
      state.isPaginationDisabled = false;
      state.offset = 0;
      state.listData = [];
      state.loading = 'force-pending';
    },
    updateSpecialistBy: (state, action: PayloadAction<UpdatePayload>) => {
      const { field, value, data } = action.payload;
      if (state?.listData) {
        state.listData = updateArray<ISpecialist>(
          state.listData,
          (specialist) => specialist[field] === value, // Condition to find the specialist
          data // Data to update
        );
      }
    },
    setFiltersValues: (state, action: PayloadAction<Record<string, FilterValue>>) => {
      state.filters.values = action.payload;
      // reset loading state for specialists list
      state.loading = 'idle';
      state.offset = 0;
      state.listData = [];
    },
    setTotalItems: (state, action: PayloadAction<number | undefined>) => {
      state.filters.totalItems = action.payload;
    },
    setFiltersLoading: (state, action: PayloadAction<'idle' | 'pending' | 'succeeded' | 'failed'>) => {
      state.filters.loading = action.payload;
    },
    setFiltersItems: (state, action: PayloadAction<FilterItem[]>) => {
      state.filters.items = action.payload;
    },
    incrementSessionCount: (state, action: PayloadAction<string>) => {
      if (state.listData) {
        state.listData = state.listData.map((item) => {
          if (item.id === action.payload) {
            return { ...item, session_count_by_3_days: (item.session_count_by_3_days ?? 0) + 1 };
          }
          return item;
        });
      }
    },
    setSort: (state, action: PayloadAction<Sort>) => {
      state.sort = action.payload;
      state.loading = 'force-pending';
      state.offset = 0;
      state.listData = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSpecialistsList.pending, (state) => {
        if (state.loading === 'force-pending') return;
        state.loading = 'pending';
      })
      .addCase(fetchSpecialistsList.fulfilled, (state, action) => {
        const specialists = action?.payload?.list;
        state.listData = uniqBy([...(state?.listData ?? []), ...(specialists ?? [])], 'id');
        state.total = action?.payload?.total;
        if (
          !specialists?.length ||
          specialists?.length < SPECIALISTS_COUNT_PER_PAGE ||
          (action?.payload?.total && action?.payload?.total <= state.listData?.length)
        ) {
          state.isPaginationDisabled = true;
        } else {
          state.isPaginationDisabled = false;
        }

        state.offset = state.offset + state.limit;
        state.loading = 'succeeded';
      })
      .addCase(fetchSpecialistsList.rejected, (state) => {
        state.loading = 'failed';
      })
      .addCase(addSpecialistToFavourite.pending, (state, action) => {
        const specialist = state?.listData?.find((el) => el.id === action.meta.arg.id);
        if (specialist) specialist.favourite = true;
      })
      .addCase(removeSpecialistFromFavourite.pending, (state, action) => {
        const specialist = state?.listData?.find((el) => el.id === action.meta.arg.id);
        if (specialist) specialist.favourite = false;
      })
      .addCase(fetchSpecialistByID.pending, (state) => {
        if (state.listData) {
          state.loading = 'pending';
        }
      })
      .addCase(fetchSpecialistByID.rejected, (state) => {
        if (state.listData) {
          state.loading = 'failed';
        }
      })
      .addCase(fetchSpecialistByID.fulfilled, (state, action) => {
        if (state.listData) {
          state.listData = state.listData.map((item) => {
            if (item.id === action.payload.id) {
              return {
                ...item,
                ...action.payload,
              };
            }
            return item;
          });
          state.loading = 'succeeded';
        }
      })
      .addCase(fetchLastContract.fulfilled, (state, action) => {
        if (!state.listData || !action.payload) return;
        state.listData = state.listData?.map((item) => {
          if (item.id === action.payload.specialist_id) {
            return {
              ...item,
              client: {
                specialist_client_contract_status: action.payload.status,
                specialist_client_contract_id: action.payload.id,
              },
            };
          }
          return item;
        });
      })
      .addCase(fetchSpecialistsFilters.fulfilled, (state, action) => {
        state.filters.items = action.payload.filters;
        state.filters.values = action.payload.filtersValues;
        state.goals = action.payload.goals ?? [];
        state.filters.loading = 'succeeded';
      })
      .addCase(fetchSpecialistsFilters.rejected, (state) => {
        state.filters.loading = 'failed';
      })
      .addCase(fetchSpecialistsFilters.pending, (state) => {
        state.filters.loading = 'pending';
        state.filters.values = {};
        state.filters.items = [];
        state.goals = [];
      })
      .addCase(fetchSpecialistsBackupList.fulfilled, (state, action) => {
        state.backup.listData = action.payload.list;
        state.backup.loading = 'succeeded';
      })
      .addCase(fetchSpecialistsBackupList.rejected, (state) => {
        state.backup.loading = 'failed';
      })
      .addCase(fetchSpecialistsBackupList.pending, (state) => {
        state.backup.loading = 'pending';
      })
      .addCase(updateReview.fulfilled, (state, action) => {
        state.listData =
          state.listData?.map((item) => {
            if (item.id === action.payload.specialist_id) {
              return {
                ...item,
                reviews_count: action.payload.specialist.reviews_count,
                reviews_average_rating: action.payload.specialist.reviews_average_rating,
                reviews_count_by_stars: action.payload.specialist.reviews_count_by_stars,
              };
            }
            return item;
          }) ?? null;
      });
  },
});

export const {
  resetSpecialist,
  resetOffset,
  updateSpecialistBy,
  setFiltersValues,
  setTotalItems,
  setFiltersLoading,
  setFiltersItems,
  incrementSessionCount,
  setSort,
  resetPagination,
} = specialistsReducer.actions;

export default specialistsReducer.reducer;
